// Bootstrap: load commander, register all 18 subcommands, parse argv.
// Each command lives in src/commands/<name>.ts as a function that accepts the
// commander Command instance and registers itself.
import { Command } from "commander";
import { registerAddCommand } from "./commands/add-repo.js";
import { registerAiCommand } from "./commands/ai.js";
import { registerCommConnectCommand } from "./commands/comm-connect.js";
import { registerDebateCommand } from "./commands/debate.js";
import { registerDoctorCommand } from "./commands/doctor.js";
import { registerGitnexusCommand } from "./commands/gitnexus.js";
import { registerInitCommand } from "./commands/init.js";
import { registerLoginCommand } from "./commands/login.js";
import { registerLogoutCommand } from "./commands/logout.js";
import { registerMcpRunCommand } from "./commands/mcp-run.js";
import { registerPackCommand } from "./commands/pack-status-and-version-check.js";
import { registerPrintIdTokenCommand } from "./commands/print-id-token.js";
import { registerRestoreCommand } from "./commands/restore.js";
import { registerReviewCommand } from "./commands/review.js";
import { registerScanCommand } from "./commands/scan.js";
import { registerStatusCommand } from "./commands/status.js";
import { registerSyncCommand } from "./commands/sync.js";
import { registerToolsCommand } from "./commands/tools.js";
import { registerUninstallCommand } from "./commands/uninstall.js";
import { registerUpdateCommand } from "./commands/update.js";
import { printAvatarBanner, renderAvatarBanner } from "./lib/avatar-ascii-banner.js";
import { readCliVersion } from "./lib/read-cli-version-from-package-json.js";

const CLI_VERSION = readCliVersion();

const program = new Command();

program
  .name("avatar")
  .description("AI harness CLI for NAL Vietnam engineering")
  .version(CLI_VERSION, "-v, --version", "Hiển thị phiên bản Avatar CLI")
  // Override mặc định của commander để in banner kèm version. Commander cho phép
  // custom output qua configureOutput nhưng cách đơn giản nhất là intercept ở argv.
  .addHelpText(
    "beforeAll",
    () =>
      `\n${renderAvatarBanner({ tagline: `v${CLI_VERSION} · AI harness CLI for NAL Vietnam` })}\n\n`,
  );

// In banner khi user gọi `avatar -v` / `avatar --version`. Banner đã chứa
// version trong tagline nên ta short-circuit luôn, không để commander in thêm
// dòng "1.0.1" dư thừa.
//
// Chỉ bắt khi -v/--version là token ĐẦU TIÊN (không có subcommand). Dùng
// firstArg thay .includes() toàn argv — tránh nuốt nhầm flag của lệnh con.
// (Lưu ý: lệnh con KHÔNG được đặt tên option là `--version` vì commander coi đó
// là global version flag — vd `sync` dùng `--pack-version`, không phải `--version`.)
const firstArg = process.argv[2];
const isVersionCall = firstArg === "-v" || firstArg === "--version";
if (isVersionCall) {
  printAvatarBanner({ tagline: `v${CLI_VERSION} · AI harness CLI for NAL Vietnam` });
  process.exit(0);
}

// Register all commands. Order matches Chapter 09 spec in implementation doc.
registerLoginCommand(program);
registerLogoutCommand(program);
registerInitCommand(program);
registerSyncCommand(program);
registerScanCommand(program);
registerReviewCommand(program);
registerStatusCommand(program);
registerDoctorCommand(program);
registerRestoreCommand(program);
registerToolsCommand(program);
registerDebateCommand(program);
registerMcpRunCommand(program);
registerAiCommand(program);
registerCommConnectCommand(program);
registerGitnexusCommand(program);
registerPackCommand(program);
registerAddCommand(program);
registerUninstallCommand(program);
registerUpdateCommand(program);
registerPrintIdTokenCommand(program);

program.parseAsync(process.argv).catch((err: unknown) => {
  // Top-level error sink. Individual commands should handle their own errors;
  // anything reaching here is unexpected.
  const msg = err instanceof Error ? err.message : String(err);
  process.stderr.write(`✗ Lỗi không xử lý được: ${msg}\n`);
  process.exit(1);
});
